import type { JSX } from "react";

import type { AmsHealth, ReadyChecks } from "../api/bench";
import type { GiteaHealth } from "../api/repositories";

interface HealthPanelProps {
  health: AmsHealth | null;
  /** Null when the environment has no Gitea configured. */
  gitea: GiteaHealth | null;
  loading: boolean;
  onRefresh: () => void;
}

const DEPENDENCIES: ReadonlyArray<{
  key: "neo4j" | "redis" | "tei";
  label: string;
  note: string;
}> = [
  { key: "neo4j", label: "Neo4j", note: "hard dependency — the graph itself" },
  { key: "redis", label: "Redis", note: "hard dependency — working memory, jobs, idempotency" },
  { key: "tei", label: "TEI embeddings", note: "soft — recall degrades, reads still work" },
];

/**
 * The Memory Service's own readiness, in plain terms. This is the first thing to check when
 * something behaves oddly: a down TEI silently degrades recall, and a stub LLM makes every
 * ask answer canned.
 */
export function HealthPanel({
  health,
  gitea,
  loading,
  onRefresh,
}: HealthPanelProps): JSX.Element {
  return (
    <section className="panel">
      <header className="panel__header">
        <h2>Memory Service health</h2>
        <button type="button" onClick={onRefresh} disabled={loading}>
          {loading ? "Checking…" : "Re-check"}
        </button>
      </header>

      {health === null ? (
        <p className="muted">Not checked yet.</p>
      ) : !health.reachable ? (
        <div className="notice notice--error">
          <strong>Not reachable.</strong> {health.detail ?? "No detail was returned."}
        </div>
      ) : (
        <>
          <p className="health__status">
            <StatusBadge status={health.status} />
            {health.status === "degraded" && (
              <span className="muted">
                {" "}
                Reads still work, but anything needing embeddings will not.
              </span>
            )}
            {health.status === "not_ready" && (
              <span className="muted"> A hard dependency is down; most calls will fail.</span>
            )}
          </p>

          <ul className="checks">
            {DEPENDENCIES.map(({ key, label, note }) => (
              <li key={key}>
                <Tick ok={health.checks?.[key] ?? null} />
                <span className="checks__label">{label}</span>
                <span className="muted">{note}</span>
                {errorFor(health.checks, key) && (
                  <code className="checks__error">{errorFor(health.checks, key)}</code>
                )}
              </li>
            ))}
          </ul>

          {health.llm && (
            <p className={health.llm.stub ? "notice notice--warning" : "muted"}>
              {health.llm.stub ? (
                <>
                  <strong>The LLM is the stub.</strong> Answers from &ldquo;Ask a question&rdquo;
                  will be canned, not real. Set a provider API key on the deployment for genuine
                  answers.
                </>
              ) : (
                <>
                  LLM: {health.llm.provider} / {health.llm.model}
                </>
              )}
            </p>
          )}
        </>
      )}

      {gitea && <GiteaRow gitea={gitea} />}
    </section>
  );
}

/**
 * Gitea, listed under its own heading.
 *
 * Deliberately not mixed into the dependency list above: those come from the Memory Service's own
 * readiness check, and it does not look at Gitea. This is the bench's own probe, and it answers a
 * different question -- whether provisioning a repository will work.
 */
function GiteaRow({ gitea }: { gitea: GiteaHealth }): JSX.Element {
  return (
    <>
      <h3 className="health__subhead">Gitea &mdash; checked by the bench, not by the service</h3>
      <ul className="checks">
        <li>
          <Tick ok={gitea.reachable} />
          <span className="checks__label">Reachable</span>
          <span className="muted">
            {gitea.status ? `status ${gitea.status}` : "needed to create a repository to attach"}
            {gitea.version && ` · v${gitea.version}`}
          </span>
        </li>
        <li>
          <Tick ok={gitea.can_provision} />
          <span className="checks__label">Can provision</span>
          <span className="muted">
            {gitea.can_provision
              ? "the configured token can create repositories"
              : "no usable token, so repositories must be attached by hand"}
          </span>
        </li>
      </ul>
      {gitea.detail && <p className="muted">{gitea.detail}</p>}
    </>
  );
}

function errorFor(checks: ReadyChecks | null, key: "neo4j" | "redis" | "tei"): string | null {
  if (!checks) {
    return null;
  }
  return checks[`${key}_error`] ?? null;
}

function StatusBadge({ status }: { status: string | null }): JSX.Element {
  const tone = status === "ready" ? "on" : status === "degraded" ? "warn" : "danger";
  return <span className={`flag flag--${tone}`}>{status ?? "unknown"}</span>;
}

function Tick({ ok }: { ok: boolean | null }): JSX.Element {
  if (ok === null) {
    return <span className="tick tick--unknown">?</span>;
  }
  return (
    <span className={ok ? "tick tick--ok" : "tick tick--bad"} aria-label={ok ? "up" : "down"}>
      {ok ? "✓" : "✗"}
    </span>
  );
}
